/**
 * 新建批量任务弹窗
 */
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Select, Form, Divider, message } from 'antd';
import { useBatchTaskFuncTypes } from '@/utils/useBatchTaskEnums';
import { createBatchOptTask } from '@/service/batchOpt';
import AebParamsWriteForm, { AebParamsWriteFormRef } from '../components/AebParamsWriteForm';
import CalibrationParamsWriteForm, { CalibrationParamsWriteFormRef } from '../components/CalibrationParamsWriteForm';
import CalibrationParamsQueryForm, { CalibrationParamsQueryFormRef } from '../components/CalibrationParamsQueryForm';
import AebDeviceConfigWriteForm, { AebDeviceConfigWriteFormRef } from '../components/AebDeviceConfigWriteForm';
import AebFunctionSwitchWriteForm, { AebFunctionSwitchWriteFormRef } from '../components/AebFunctionSwitchWriteForm';

const { TextArea } = Input;

interface CreateBatchTaskModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void; // 操作成功后的回调
}

const CreateBatchTaskModal: React.FC<CreateBatchTaskModalProps> = (props) => {
  const { visible, onClose, onSuccess } = props;
  const [form] = Form.useForm();
  const [selectedFuncType, setSelectedFuncType] = useState<string | undefined>();
  const [rawInput, setRawInput] = useState('');
  const [delimiter, setDelimiter] = useState<string>('space');
  const [submitting, setSubmitting] = useState(false);

  // 各个表单组件的ref
  const aebParamsFormRef = useRef<AebParamsWriteFormRef>(null);
  const calibrationParamsFormRef = useRef<CalibrationParamsWriteFormRef>(null);
  const calibrationParamsQueryFormRef = useRef<CalibrationParamsQueryFormRef>(null);
  const aebDeviceConfigFormRef = useRef<AebDeviceConfigWriteFormRef>(null);
  const aebFunctionSwitchFormRef = useRef<AebFunctionSwitchWriteFormRef>(null);

  // 获取功能类型枚举
  const { funcTypes } = useBatchTaskFuncTypes();

  // 解析SN号
  const getParsedSNs = (input: string, delim: string) => {
    if (!input) return [];
    let splitRegex;
    switch (delim) {
      case 'space':
        splitRegex = /\s+/;
        break;
      case 'comma':
        splitRegex = /[,，]/;
        break;
      case 'hash':
        splitRegex = /#/;
        break;
      default:
        splitRegex = /\s+/;
    }
    return input.split(splitRegex).map(s => s.trim()).filter(Boolean);
  };

  const parsedSNs = getParsedSNs(rawInput, delimiter);

  // 提交按钮处理
  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // 验证SN号
      if (parsedSNs.length === 0) {
        message.error('请输入有效的设备SN号');
        return;
      }

      // 处理设备SN号：使用解析后的结果
      const deviceSns = parsedSNs.join(',');

      // 生成任务名称（如果没有提供）
      const taskName = values.taskName || `${getSelectedFuncTypeName()}_${new Date().getTime()}`;

      setSubmitting(true);

      // 根据任务类型获取对应表单的参数内容
      let paramContent: string | null | undefined;
      try {
        switch (selectedFuncType) {
          case 'AEB_CONFIG_SET':
            paramContent = await aebParamsFormRef.current?.getSubmitString();
            break;
          case 'AEB_CALIBRATION_SET':
            paramContent = await calibrationParamsFormRef.current?.getSubmitString();
            break;
          case 'AEB_CALIBRATION_GET':
            paramContent = await calibrationParamsQueryFormRef.current?.getSubmitString();
            break;
          case 'AEB_SWITCH_CONFIG_SET':
            paramContent = await aebDeviceConfigFormRef.current?.getSubmitString();
            break;
          case 'AEB_FUNCTION_SWITCH_SET':
            paramContent = await aebFunctionSwitchFormRef.current?.getSubmitString();
            break;
          default:
            // 其他类型暂不处理paramContent
            break;
        }

        // 检查是否获取到了null（表示用户没有填写任何参数）
        if (paramContent === null) {
          message.error('请选择设置项');
          setSubmitting(false);
          return;
        }
      } catch (error) {
        console.error('获取表单参数失败:', error);
        message.error('请完整填写表单参数');
        setSubmitting(false);
        return;
      }

      const requestData: any = {
        taskName,
        funcType: values.funcType,
        sns: deviceSns,
      };

      // 如果有参数内容，添加到请求中
      if (paramContent) {
        requestData.paramContent = paramContent;
      }

      const res = await createBatchOptTask(requestData);

      if (res.data.success) {
        message.success('批量任务创建成功');
        onSuccess?.();
        onClose();
      } else {
        message.error(res.data.msg || '批量任务创建失败');
      }
    } catch (error: any) {
      console.error('创建批量任务失败:', error);
      // 表单验证失败不显示错误提示
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || '批量任务创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 任务类型变化处理
  const handleFuncTypeChange = (value: string) => {
    setSelectedFuncType(value);
  };

  // 获取选中的任务类型名称
  const getSelectedFuncTypeName = () => {
    const funcType = funcTypes.find(item => item.value === selectedFuncType);
    return funcType?.label || '';
  };

  return (
    <Modal
      title="新建批量任务"
      open={visible}
      onOk={handleOk}
      onCancel={onClose}
      width="80%"
      destroyOnClose
      okText="提交"
      cancelText="取消"
      confirmLoading={submitting}
    >
      <Form
        form={form}
        layout="vertical"
      >
        {/* 任务名称（可选） */}
        <Form.Item
          label="任务名称"
          name="taskName"
        >
          <Input placeholder="请输入任务名称" />
        </Form.Item>

        {/* 第一部分：设备SN号输入 */}
        <Form.Item
          label="设备SN号"
          required
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            {/* 左侧：输入框 */}
            <div style={{ flex: 1 }}>
              <TextArea
                placeholder="请输入设备SN号"
                rows={10}
                value={rawInput}
                onChange={(e) => setRawInput(e.target.value)}
                style={{ resize: 'none' }}
              />
            </div>

            {/* 中间：分隔符选择 */}
            <div style={{ width: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: '40px' }}>
              <div style={{ marginBottom: '8px', textAlign: 'center', color: '#666' }}>选择分隔符</div>
              <Select
                value={delimiter}
                onChange={setDelimiter}
                options={[
                  { label: '空格/换行', value: 'space' },
                  { label: '逗号', value: 'comma' },
                  { label: '#号', value: 'hash' },
                ]}
                style={{ width: '100%' }}
              />
            </div>

            {/* 右侧：结果展示 */}
            <div style={{
              flex: 1,
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              padding: '12px',
              height: '232px', // Match rows={10} height approx
              overflowY: 'auto',
              backgroundColor: '#fafafa'
            }}>
              {parsedSNs.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {parsedSNs.map((sn, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: '#e6f7ff',
                        border: '1px solid #91d5ff',
                        borderRadius: '4px',
                        color: '#1890ff',
                        fontSize: '14px',
                        display: 'inline-block'
                      }}
                    >
                      {sn}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#999', textAlign: 'center', marginTop: '80px' }}>
                  解析结果将显示在这里
                </div>
              )}
            </div>
          </div>
        </Form.Item>
        <Divider />
        {/* 第二部分：任务类型选择 */}
        <Form.Item
          label="任务类型"
          name="funcType"
          rules={[{ required: true, message: '请选择任务类型' }]}
        >
          <Select
            placeholder="请选择任务类型"
            options={funcTypes}
            onChange={handleFuncTypeChange}
          />
        </Form.Item>
        <Divider />
        {/* 第三部分：任务相关表单 */}
        {selectedFuncType && (
          <>
            {selectedFuncType === 'AEB_CONFIG_SET' && (
              <AebParamsWriteForm ref={aebParamsFormRef} />
            )}
            {selectedFuncType === 'AEB_CALIBRATION_SET' && (
              <CalibrationParamsWriteForm ref={calibrationParamsFormRef} />
            )}
            {selectedFuncType === 'AEB_CALIBRATION_GET' && (
              <CalibrationParamsQueryForm ref={calibrationParamsQueryFormRef} />
            )}
            {selectedFuncType === 'AEB_SWITCH_CONFIG_SET' && (
              <AebDeviceConfigWriteForm ref={aebDeviceConfigFormRef} />
            )}
            {selectedFuncType === 'AEB_FUNCTION_SWITCH_SET' && (
              <AebFunctionSwitchWriteForm ref={aebFunctionSwitchFormRef} />
            )}
          </>
        )}
      </Form>
    </Modal>
  );
};

export default CreateBatchTaskModal;
